import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { Typography } from "@bookips/sds";
import Link from "next/link";
import { Fragment, useEffect, useRef, useState } from "react";
/**
 * 브레드크럼 네비게이션 컴포넌트
 * - 줄바꿈 발생 시 중간 뎁스를 접어서 표시 (Folding)
 * - 1depth와 마지막 depth만 표시
 * - 폴딩된 부분 클릭 시 전체 펼침
 * - 마지막 depth가 길 경우 truncate 처리
 */ export const BreadCrumb = (param)=>{
    let { items , className , pageType ="edition"  } = param;
    const [isFolded, setIsFolded] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef(null);
    // 페이지 타입별 Position 값
    const position = pageType === "system" ? "System Category Breadcrumb" : "Edition Category Breadcrumb";
    // 줄바꿈 감지 (ResizeObserver)
    useEffect(()=>{
        const container = containerRef.current;
        if (!container || items.length <= 2) return;
        const checkWrapping = ()=>{
            if (!containerRef.current) return;
            const children = Array.from(containerRef.current.children);
            if (children.length === 0) return;
            // 첫 번째 요소와 마지막 요소의 Y 위치 비교
            const firstChildTop = children[0].offsetTop;
            const lastChildTop = children[children.length - 1].offsetTop;
            // 줄바꿈이 발생하면 folding 상태로 전환
            setIsFolded(lastChildTop > firstChildTop);
        };
        // ResizeObserver로 컨테이너 크기 변화 감지
        const resizeObserver = new ResizeObserver(()=>{
            checkWrapping();
        });
        resizeObserver.observe(container);
        checkWrapping() // 초기 체크
        ;
        return ()=>{
            resizeObserver.disconnect();
        };
    }, [
        items
    ]);
    if (!items.length) return null;
    // MX-356: 브레드크럼 아이템 클릭 핸들러
    const handleBreadcrumbClick = (item, depth)=>{
        mixpanelEvent("Navigation Item Clicked", {
            "Button URL": item.href || "",
            "Button Name": item.label,
            Depth: depth,
            Position: position
        });
    };
    // 3개 이상의 depth가 있고, folding 상태이며, 펼쳐지지 않은 경우
    const shouldFold = items.length > 2 && isFolded && !isExpanded;
    // Folding 모드: 1depth, "...", 마지막 depth만 표시
    if (shouldFold) {
        const firstItem = items[0];
        const lastItem = items[items.length - 1];
        return /*#__PURE__*/ _jsx("nav", {
            className: className,
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-1",
                children: [
                    firstItem.href ? /*#__PURE__*/ _jsx(Link, {
                        href: firstItem.href,
                        onClick: ()=>handleBreadcrumbClick(firstItem, 1),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "text-secondary hover:underline hover:text-primary whitespace-nowrap",
                            children: firstItem.label
                        })
                    }) : /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        className: "text-secondary whitespace-nowrap",
                        children: firstItem.label
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        className: "text-secondary",
                        children: "/"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setIsExpanded(true),
                        className: "hover:bg-gray-100 rounded px-1 transition-colors",
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "text-secondary",
                            children: "\xb7\xb7\xb7"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        className: "text-secondary",
                        children: "/"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        className: "text-primary whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px]",
                        children: lastItem.label
                    })
                ]
            })
        });
    }
    // 일반 모드 또는 펼쳐진 상태: 모든 depth 표시
    return /*#__PURE__*/ _jsx("nav", {
        className: className,
        children: /*#__PURE__*/ _jsx("div", {
            ref: containerRef,
            className: "flex flex-wrap items-center gap-1",
            children: items.map((item, index)=>{
                const isLast = index === items.length - 1;
                const isClickable = !isLast && item.href;
                return /*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        isClickable ? /*#__PURE__*/ _jsx(Link, {
                            href: item.href,
                            onClick: ()=>handleBreadcrumbClick(item, index + 1),
                            children: /*#__PURE__*/ _jsx(Typography, {
                                variant: "body2",
                                className: "text-secondary hover:underline hover:text-primary whitespace-nowrap",
                                children: item.label
                            })
                        }) : /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "whitespace-nowrap ".concat(isLast ? "text-primary overflow-hidden text-ellipsis max-w-[300px]" : "text-secondary"),
                            children: item.label
                        }),
                        !isLast && /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "text-secondary",
                            children: "/"
                        })
                    ]
                }, index);
            })
        })
    });
};
